// ============================================
// FILE: ContentInner2.jsx
// React Component for Dynamic Questionnaire
// Modified to show only Core Competencies (no sub-competencies)
// ============================================

import React, { useState, useEffect, useRef } from "react";
import "./homestyle.css";
import EditCont from "../../assets/images/editCon.png";
import {
  UnitQuestionsList,
  LessonQuestionsList,
  AssessmentQuestionsList,
  getCompetenciesForSelection,
} from "../../utils/QuestionsList";
import { useChatContext } from "../../context/ChatContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TextareaAutosize from "react-textarea-autosize";
import LearningSupportsQuestionnaire from "./LearningSupportsQuestionnaire";

const formatAnswersAsPlainText = (questionsList, answers) => {
  return questionsList
    .filter((q) => (answers[q.question] || "").trim() !== "")
    .map((q) => {
      const ans = answers[q.question].trim();
      return `Q: ${q.question}\nA: ${ans}\n`;
    })
    .join("\n");
};

const ContentInner2 = () => {
  const {
    initialStart,
    setTitleId,
    setInitialStart,
    setHighLightBtn
  } = useChatContext();

  // All hooks must be called unconditionally
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [nestedSelections, setNestedSelections] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState(null);
  const [multiSelectCompetencies, setMultiSelectCompetencies] = useState([]);
  const [multiSelectValues, setMultiSelectValues] = useState({}); // For multi_select and checkbox types
  const [domainRatings, setDomainRatings] = useState({}); // For domain_ratings type
  const [otherTextValues, setOtherTextValues] = useState({}); // For "other" text inputs
  const [supportEstimatorValues, setSupportEstimatorValues] = useState({}); // For support_estimator type
  
  // State for dropdown toggle
  const [isCoreDropdownOpen, setIsCoreDropdownOpen] = useState(false);
  const coreDropdownRef = useRef(null);
  
  // State for animation class
  const [addClass, setAddClass] = useState(false);

  const navigate = useNavigate();

  // Define questions list and final button (needed for useEffect dependencies)
  const questionsList =
    initialStart === "UnitPlanning"
      ? [...UnitQuestionsList]
      : initialStart === "LessonPlanning"
      ? [...LessonQuestionsList]
      : initialStart === "Assessment"
      ? [...AssessmentQuestionsList]
      : [];

  const FinalButton =
    initialStart === "UnitPlanning"
      ? "Full Unit Plan"
      : initialStart === "LessonPlanning"
      ? "Full Lesson Plan"
      : initialStart === "Assessment"
      ? "Assessment Plan"
      : "";

  // Helper function to check if we should treat has_designation as "no"
  // This handles the case where user selects "unsure" then "continue without designation"
  const shouldTreatDesignationAsNo = () => {
    if (initialStart === "LearningSupports") return false;
    const designationQuestion = questionsList.find(dq => dq.key === "has_designation");
    if (!designationQuestion) return false;
    
    const designationAnswer = answers[designationQuestion.question];
    if (!designationAnswer) return false;
    
    const answerLower = designationAnswer.toLowerCase().trim();
    
    // Direct "no" answer
    if (answerLower === "no") {
      return true;
    }
    
    // "unsure" + clarification to continue without designation
    if (answerLower === "not sure" || answerLower === "unsure") {
      const clarificationQuestion = questionsList.find(dq => dq.key === "clarification_choice");
      if (clarificationQuestion) {
        const clarificationAnswer = answers[clarificationQuestion.question];
        if (clarificationAnswer) {
          const clarificationLower = clarificationAnswer.toLowerCase();
          if (clarificationLower.includes("continue without designation") || 
              clarificationLower.includes("support-based path") ||
              clarificationLower.includes("continue_no")) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  // Filter questions based on conditional logic
  const getFilteredQuestionsList = () => {
    if (initialStart === "LearningSupports" || questionsList.length === 0) return [];
    const filtered = [];
    
    for (let i = 0; i < questionsList.length; i++) {
      const q = questionsList[i];
      
      if (!q.dependsOn) {
        filtered.push(q);
        continue;
      }
      
      // Find the question this depends on (check both key and question text)
      const dependsOnQuestion = filtered.find(dq => 
        dq.key === q.dependsOn || dq.question === q.dependsOn
      ) || questionsList.find(dq => 
        dq.key === q.dependsOn || dq.question === q.dependsOn
      );
      
      if (!dependsOnQuestion) {
        filtered.push(q);
        continue;
      }
      
      const dependsOnAnswer = answers[dependsOnQuestion.question];
      if (!dependsOnAnswer || dependsOnAnswer.trim() === "") {
        continue; // Skip this question
      }
      
      // Check if answer matches the required value
      if (q.dependsOnValue) {
        const answerLower = dependsOnAnswer.toLowerCase().trim();
        const valueLower = q.dependsOnValue.toLowerCase().trim();
        
        // Special handling: if dependsOn is "has_designation" and value is "no",
        // check both direct "no" answer and "unsure" + "continue_no" path
        if (q.dependsOn === "has_designation" && valueLower === "no") {
          if (shouldTreatDesignationAsNo()) {
            filtered.push(q);
          }
        } else if (valueLower === "yes") {
          // For "yes", only show if direct yes answer (not from clarification path)
          if (answerLower === "yes" || answerLower.includes("yes")) {
            filtered.push(q);
          }
        } else {
          // For other specific values, check exact match
          if (answerLower === valueLower || answerLower.includes(valueLower)) {
            filtered.push(q);
          }
        }
      } else {
        // If no specific value required, just check if answer exists
        filtered.push(q);
      }
    }
    
    return filtered;
  };

  const filteredQuestionsList = getFilteredQuestionsList();
  const currentQuestion = filteredQuestionsList[currentIndex];
  const currentAnswer = answers[currentQuestion?.question] || "";
  const isAnswerFilled = currentAnswer.trim() !== "" || 
    (currentQuestion?.type === "multi_select" && multiSelectValues[currentQuestion?.key]?.length > 0) ||
    (currentQuestion?.type === "checkbox" && multiSelectValues[currentQuestion?.key]?.length > 0) ||
    (currentQuestion?.type === "domain_ratings" && Object.keys(domainRatings[currentQuestion?.key] || {}).length > 0) ||
    (currentQuestion?.type === "support_estimator" && currentAnswer.trim() !== "");

  // Effect to handle dependent questions (only for non-LearningSupports)
  useEffect(() => {
    if (initialStart === "LearningSupports") return;
    if (currentQuestion?.dependsOn) {
      const dependentQuestionKey = currentQuestion.dependsOn;
      
      if (dependentQuestionKey === "grade_subject_selection") {
        const selections = nestedSelections[dependentQuestionKey];
        
        if (selections && selections.length >= 2) {
          const gradeValue = selections[0];
          const subjectValue = selections[1];
          
          const competencies = getCompetenciesForSelection(gradeValue, subjectValue);
          setDynamicOptions(competencies);
        } else {
          setDynamicOptions([]);
        }
      }
    } else {
      setDynamicOptions(null);
    }
  }, [initialStart, currentIndex, currentQuestion, nestedSelections]);

  // Restore multi-select state when navigating (only for non-LearningSupports)
  useEffect(() => {
    if (initialStart === "LearningSupports") return;
    if (currentQuestion?.type === "multi_select_nested") {
      const existingAnswer = answers[currentQuestion?.question];
      if (existingAnswer && existingAnswer.trim()) {
        const competencyLabels = existingAnswer.split("; ");
        const restored = [];
        
        const currentOptions = dynamicOptions !== null ? dynamicOptions : currentQuestion?.options;
        
        competencyLabels.forEach(label => {
          const coreComp = currentOptions?.find(c => c.label === label.trim());
          if (coreComp) {
            restored.push(coreComp);
          }
        });
        
        setMultiSelectCompetencies(restored);
      }
    } else if (currentQuestion?.type === "multi_select" || currentQuestion?.type === "checkbox") {
      const questionKey = currentQuestion?.key;
      if (questionKey) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.trim()) {
          const labels = existingAnswer.split(", ").map(l => l.trim());
          const restored = currentQuestion.options
            .filter(opt => {
              // Check if label matches or if it's an "other" entry
              return labels.some(label => 
                label === opt.label || 
                (opt.value === "other" && label.startsWith("Other:"))
              );
            })
            .map(opt => ({ value: opt.value, label: opt.label }));
          
          setMultiSelectValues(prev => ({
            ...prev,
            [questionKey]: restored
          }));
          
          // Extract "other" text if present
          const otherEntry = labels.find(l => l.startsWith("Other:"));
          if (otherEntry) {
            const otherText = otherEntry.replace("Other:", "").trim();
            setOtherTextValues(prev => ({
              ...prev,
              [questionKey]: otherText
            }));
          }
        }
      }
    } else if (currentQuestion?.type === "domain_ratings") {
      const questionKey = currentQuestion?.key;
      if (questionKey) {
        const existingAnswer = answers[currentQuestion?.question];
        if (existingAnswer && existingAnswer.trim()) {
          const ratings = {};
          const parts = existingAnswer.split("; ");
          parts.forEach(part => {
            const [domainPart, ratingPart] = part.split(": ");
            if (domainPart && ratingPart) {
              const domain = currentQuestion.domains.find(d => d.name === domainPart.trim());
              if (domain) {
                const rating = currentQuestion.ratingOptions.find(r => r.label === ratingPart.trim());
                if (rating) {
                  ratings[domain.key] = { value: rating.value, label: rating.label };
                }
              }
            }
          });
          setDomainRatings(prev => ({
            ...prev,
            [questionKey]: ratings
          }));
        }
      }
    }
  }, [initialStart, currentIndex, currentQuestion, answers, dynamicOptions]);

  // Close dropdown when clicking outside (only for non-LearningSupports)
  useEffect(() => {
    if (initialStart === "LearningSupports") return;
    const handleClickOutside = (event) => {
      if (coreDropdownRef.current && !coreDropdownRef.current.contains(event.target)) {
        setIsCoreDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [initialStart]);

  // If LearningSupports (or unset home state), render the separate component (after all hooks)
  if (initialStart === "LearningSupports" || !initialStart) {
    return <LearningSupportsQuestionnaire />;
  }

  const handleInputChange = (e) => {
    setAnswers({
      ...answers,
      [currentQuestion?.question]: e.target.value,
    });
  };

  // Handle text input with validation for student initials (only alphabets)
  const handleTextInputChange = (e) => {
    const value = e.target.value;
    // For student initials, only allow alphabets and spaces
    if (currentQuestion?.key === "student_initials") {
      // Allow only letters and spaces, convert to uppercase
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
      setAnswers({
        ...answers,
        [currentQuestion?.question]: filteredValue,
      });
    } else {
      // For other text inputs, allow normal input
      setAnswers({
        ...answers,
        [currentQuestion?.question]: value,
      });
    }
  };

  // Handle number input with validation for student age (no negative, no 'e')
  const handleNumberInputChange = (e) => {
    const value = e.target.value;
    // For student age, prevent negative numbers and 'e' character
    if (currentQuestion?.key === "student_age") {
      // Remove 'e', 'E', '+', '-' and any non-numeric characters except decimal point
      let filteredValue = value.replace(/[eE\+\-]/g, '');
      // Only allow positive integers (no decimals for age)
      filteredValue = filteredValue.replace(/[^0-9]/g, '');
      // Prevent negative by ensuring it's always positive
      const numValue = parseInt(filteredValue) || '';
      setAnswers({
        ...answers,
        [currentQuestion?.question]: numValue.toString(),
      });
    } else {
      // For other number inputs, allow normal input
      setAnswers({
        ...answers,
        [currentQuestion?.question]: value,
      });
    }
  };

  // Handle checkbox/multi-select toggle
  const handleCheckboxToggle = (optionValue, optionLabel) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;

    const currentSelections = multiSelectValues[questionKey] || [];
    const isSelected = currentSelections.some(item => item.value === optionValue);

    let updatedSelections;
    if (isSelected) {
      updatedSelections = currentSelections.filter(item => item.value !== optionValue);
      // Clear "other" text if "other" option is deselected
      if (optionValue === "other") {
        setOtherTextValues({
          ...otherTextValues,
          [questionKey]: ""
        });
      }
    } else {
      updatedSelections = [...currentSelections, { value: optionValue, label: optionLabel }];
    }

    setMultiSelectValues({
      ...multiSelectValues,
      [questionKey]: updatedSelections
    });

    // Update answer text
    const otherText = otherTextValues[questionKey];
    const answerText = updatedSelections
      .map(item => {
        if (item.value === "other" && otherText) {
          return `Other: ${otherText}`;
        }
        return item.label;
      })
      .join(", ");
    
    setAnswers({
      ...answers,
      [currentQuestion?.question]: answerText
    });
  };

  // Handle radio button selection
  const handleRadioChange = (optionValue, optionLabel) => {
    setAnswers({
      ...answers,
      [currentQuestion?.question]: optionLabel
    });
  };

  // Handle domain rating change
  const handleDomainRatingChange = (domainKey, ratingValue, ratingLabel) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;

    const currentRatings = domainRatings[questionKey] || {};
    currentRatings[domainKey] = { value: ratingValue, label: ratingLabel };

    setDomainRatings({
      ...domainRatings,
      [questionKey]: currentRatings
    });

    // Format answer text
    const answerText = Object.entries(currentRatings)
      .map(([domain, rating]) => {
        const domainName = currentQuestion.domains.find(d => d.key === domain)?.name || domain;
        return `${domainName}: ${rating.label}`;
      })
      .join("; ");

    setAnswers({
      ...answers,
      [currentQuestion?.question]: answerText
    });
  };

  // Handle "other" text input
  const handleOtherTextChange = (questionKey, value) => {
    setOtherTextValues({
      ...otherTextValues,
      [questionKey]: value
    });

    // Update answer if "other" is selected
    const currentSelections = multiSelectValues[questionKey] || [];
    const otherSelected = currentSelections.some(item => item.value === "other");
    if (otherSelected) {
      const otherText = value ? `Other: ${value}` : "Other";
      const answerText = currentSelections
        .filter(item => item.value !== "other")
        .map(item => item.label)
        .concat(value ? [otherText] : ["Other"])
        .join(", ");
      setAnswers({
        ...answers,
        [currentQuestion?.question]: answerText
      });
    }
  };

  // Check if option is selected (for checkbox/multi_select)
  const isOptionSelected = (optionValue) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return false;
    const selections = multiSelectValues[questionKey] || [];
    return selections.some(item => item.value === optionValue);
  };

  // Get domain rating for a specific domain
  const getDomainRating = (domainKey) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return null;
    return domainRatings[questionKey]?.[domainKey]?.value || null;
  };

  // Handle support estimator input
  const handleSupportEstimatorChange = (fieldKey, value) => {
    const questionKey = currentQuestion?.key;
    if (!questionKey) return;

    const currentValues = supportEstimatorValues[questionKey] || {};
    const updatedValues = {
      ...currentValues,
      [fieldKey]: value ? parseInt(value) || 0 : 0
    };

    setSupportEstimatorValues({
      ...supportEstimatorValues,
      [questionKey]: updatedValues
    });

    // Calculate total hours per year
    const totalMinutesPerWeek = Object.values(updatedValues).reduce((sum, val) => sum + (val || 0), 0);
    const totalHoursPerYear = (totalMinutesPerWeek * 40) / 60; // Assuming 40 weeks per school year

    // Format answer
    const breakdown = currentQuestion.fields
      .map(field => `${field.label}: ${updatedValues[field.key] || 0} ${field.unit}`)
      .join("; ");
    
    const answerText = `${breakdown} | Total: ${totalHoursPerYear.toFixed(1)} hours/year`;
    
    setAnswers({
      ...answers,
      [currentQuestion?.question]: answerText
    });
  };

  // Handle core competency checkbox toggle
  const handleCoreCheckboxToggle = (coreComp) => {
    const isSelected = multiSelectCompetencies.some(
      comp => comp.value === coreComp.value
    );

    let updatedCompetencies;
    if (isSelected) {
      // Remove if already selected
      updatedCompetencies = multiSelectCompetencies.filter(
        comp => comp.value !== coreComp.value
      );
    } else {
      // Add new selection
      updatedCompetencies = [...multiSelectCompetencies, coreComp];
    }

    setMultiSelectCompetencies(updatedCompetencies);

    // Format answer as semicolon-separated list
    const formattedAnswer = updatedCompetencies
      .map(comp => comp.label)
      .join("; ");

    setAnswers({
      ...answers,
      [currentQuestion?.question]: formattedAnswer
    });
  };

  // Check if a core competency is selected
  const isCoreSelected = (coreValue) => {
    return multiSelectCompetencies.some(comp => comp.value === coreValue);
  };

  // Remove a selected competency
  const removeCompetency = (index) => {
    const updatedCompetencies = multiSelectCompetencies.filter((_, idx) => idx !== index);
    setMultiSelectCompetencies(updatedCompetencies);

    const formattedAnswer = updatedCompetencies
      .map(comp => comp.label)
      .join("; ");

    setAnswers({
      ...answers,
      [currentQuestion?.question]: formattedAnswer
    });
  };


  // Render dropdown with checkboxes UI (Core Competencies Only)
  const renderDropdownWithCheckboxes = (competencies) => {
    const selectedCount = multiSelectCompetencies.length;

    return (
      <div>
        {/* Core Competencies Dropdown with Checkboxes */}
        <div style={{ marginBottom: "20px" }} ref={coreDropdownRef}>
          <div style={{ 
            fontWeight: "600",
            fontSize: "16px",
            color: "#2c68a1",
            marginBottom: "10px"
          }}>
            Select Curriculum Competencies
          </div>
          
          {/* Core Dropdown Button */}
          <div
            onClick={() => setIsCoreDropdownOpen(!isCoreDropdownOpen)}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: "16px",
              borderRadius: "10px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative"
            }}
          >
            <span style={{ color: selectedCount > 0 ? "#000" : "#6c757d" }}>
              {selectedCount > 0 
                ? `${selectedCount} core competenc${selectedCount === 1 ? 'y' : 'ies'} selected` 
                : "-- Select Curriculum Competencies --"}
            </span>
            <span style={{ fontSize: "12px" }}>{isCoreDropdownOpen ? "▲" : "▼"}</span>
          </div>

          {/* Core Dropdown Menu */}
          {isCoreDropdownOpen && (
            <div style={{
              position: "absolute",
              zIndex: 1000,
              width: "calc(100% - 60px)",
              maxHeight: "250px",
              overflowY: "auto",
              backgroundColor: "#fff",
              border: "2px solid #2c68a1",
              borderRadius: "8px",
              marginTop: "5px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              {competencies.map((coreComp, idx) => (
                <div
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCoreCheckboxToggle(coreComp);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom: idx < competencies.length - 1 ? "1px solid #dee2e6" : "none",
                    transition: "background-color 0.2s ease",
                    backgroundColor: isCoreSelected(coreComp.value) 
                      ? "#e7f3ff" 
                      : "transparent"
                  }}
                  onMouseEnter={(e) => {
                    if (!isCoreSelected(coreComp.value)) {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCoreSelected(coreComp.value)) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isCoreSelected(coreComp.value)}
                    onChange={() => {}}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginRight: "12px",
                      cursor: "pointer",
                      accentColor: "#2c68a1",
                      pointerEvents: "none"
                    }}
                  />
                  <span style={{ fontSize: "15px", fontWeight: "600" }}>{coreComp.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Competencies Summary */}
        {multiSelectCompetencies.length > 0 && (
          <div style={{
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "8px",
            padding: "15px",
            marginTop: "20px"
          }}>
            <div style={{
              color: "#155724",
              fontWeight: "600",
              marginBottom: "12px",
              fontSize: "16px"
            }}>
              ✓ {multiSelectCompetencies.length} competenc{multiSelectCompetencies.length === 1 ? 'y' : 'ies'} selected
            </div>
            
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {multiSelectCompetencies.map((comp, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    padding: "8px 14px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}
                >
                  <span>{comp.label}</span>
                  <button
                    onClick={() => removeCompetency(idx)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#721c24",
                      cursor: "pointer",
                      fontSize: "18px",
                      padding: "0",
                      lineHeight: "1"
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Recursive dropdown rendering
  const renderDropdowns = (options, level = 0, path = [], questionKey = null) => {
    if (level > 1) {
      return null;
    }

    const selectedValue = nestedSelections[`${questionKey}_${level}`] || "";

    const placeholders = [
      "-- Select Grade --",
      "-- Select Subject --"
    ];

    const labels = [
      "Select Grade",
      "Select Subject"
    ];

    let currentOptions = options;
    if (level > 0) {
      for (let i = 0; i < level; i++) {
        const selectedVal = nestedSelections[`${questionKey}_${i}`];
        if (selectedVal) {
          const found = currentOptions.find((opt) => opt.value === selectedVal);
          if (found && found.children) {
            currentOptions = found.children;
          } else {
            currentOptions = [];
            break;
          }
        } else {
          currentOptions = [];
          break;
        }
      }
    }

    return (
      <>
        <div style={{ marginBottom: "20px" }}>
          <div style={{ 
            fontWeight: "600",
            fontSize: "16px",
            color: "#2c68a1",
            marginBottom: "10px"
          }}>
            {labels[level] || "Select Option"}
          </div>
          <select
            key={`${questionKey}_${level}`}
            value={selectedValue}
            onChange={(e) => handleNestedSelect(e.target.value, level, path, options, questionKey)}
            disabled={level > 0 && path.length < level}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: "16px",
              borderRadius: "10px",
              border: "1px solid #ccc",
              backgroundColor: level > 0 && path.length < level ? "#f5f5f5" : "#fff",
              cursor: level > 0 && path.length < level ? "not-allowed" : "pointer",
              opacity: level > 0 && path.length < level ? 0.6 : 1
            }}
          >
            <option value="">{placeholders[level] || "-- Select Option --"}</option>
            {currentOptions.map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {level < 1 && currentOptions.length > 0 &&
          renderDropdowns(
            options,
            level + 1,
            selectedValue ? [...path, selectedValue] : path,
            questionKey
          )}
      </>
    );
  };

  const handleNestedSelect = (value, level, path, options, questionKey) => {
    const updatedSelections = { ...nestedSelections };
    
    if (!updatedSelections[questionKey]) {
      updatedSelections[questionKey] = [];
    }

    Object.keys(updatedSelections).forEach((key) => {
      if (key.startsWith(`${questionKey}_`)) {
        const keyLevel = parseInt(key.split('_').pop());
        if (keyLevel >= level) {
          delete updatedSelections[key];
        }
      }
    });

    updatedSelections[`${questionKey}_${level}`] = value;
    updatedSelections[questionKey] = [...path, value];
    
    setNestedSelections(updatedSelections);

    const selectedLabels = [];
    let currentOptions = options;
    
    for (let i = 0; i <= level; i++) {
      const selectedVal = updatedSelections[`${questionKey}_${i}`];
      if (!selectedVal) break;
      const found = currentOptions.find((opt) => opt.value === selectedVal);
      if (found) {
        selectedLabels.push(found.label);
        currentOptions = found.children || [];
      }
    }

    setAnswers({
      ...answers,
      [currentQuestion?.question]: selectedLabels.join(" → "),
    });
  };

 const handleNext = (important) => {
    setAddClass(true);
    
    // Validation for required questions
    if (!isAnswerFilled && important === "true") {
      toast.error("Please enter an answer before proceeding.");
      return;
    }

    // Additional validation for nested_select (Grade & Subject)
    if (currentQuestion?.type === "nested_select" && important === "true") {
      const selections = nestedSelections[currentQuestion?.key];
      
      // Check if both grade and subject are selected (need at least 2 selections)
      if (!selections || selections.length < 2) {
        if (!selections || selections.length === 0) {
          toast.error("Please select a grade before proceeding.");
        } else if (selections.length === 1) {
          toast.error("Please select a subject before proceeding.");
        }
        return;
      }
    }

    if (currentIndex < filteredQuestionsList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      const nextQuestion = filteredQuestionsList[currentIndex + 1];
      if (nextQuestion?.type !== "multi_select_nested") {
        setMultiSelectCompetencies([]);
        setIsCoreDropdownOpen(false);
      }
    } else {
      const formatedata = formatAnswersAsPlainText(filteredQuestionsList, answers);
      console.log("answers", answers);
      console.log("formatedata", formatedata);
      navigate("/afterchatbot", { state: { answers, formatedata } });
    }
  };


  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      
      // Reset multi-select states when going back from a non-multi-select question
      const previousQuestion = filteredQuestionsList[currentIndex - 1];
      if (previousQuestion?.type !== "multi_select_nested") {
        setMultiSelectCompetencies([]);
        setIsCoreDropdownOpen(false);
      }
    } else if (currentIndex === 0) {
      // When on first question, clear session and navigate to home
      sessionStorage.setItem("titleId", "");
      setTitleId("");
      setInitialStart(false);
      setHighLightBtn(false);
      navigate("/");
    }
  };


  const currentOptions = dynamicOptions !== null ? dynamicOptions : currentQuestion?.options;

  return (
    <div className={`${addClass ? "multipleChoice shrink" : ""} home-inner-page content`} id="content">
      <div className="homme-outer-containers">
        <div className="content-innerss scsbc-chartboart">
          {currentQuestion?.section && (
            <div style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#2c68a1",
              marginBottom: "15px",
              paddingBottom: "10px",
              borderBottom: "2px solid #2c68a1"
            }}>
              {currentQuestion.section}
            </div>
          )}
          <h6>
            Q.{currentIndex + 1} {currentQuestion?.question}
            {currentQuestion?.important === "true" && (
              <span style={{ color: "red", fontWeight: "bold" }}> *</span>
            )}
          </h6>

          <div className="edit-content-answer">
            {currentQuestion?.dependsOn && 
             (currentQuestion?.type === "multi_select_nested" || currentQuestion?.type === "nested_select") &&
             (!currentOptions || currentOptions.length === 0) ? (
              <div style={{ 
                padding: "15px", 
                backgroundColor: "#fff3cd", 
                border: "1px solid #ffc107",
                borderRadius: "10px",
                color: "#856404"
              }}>
                <p style={{ margin: 0 }}>
                  ⚠️ Please complete the previous question first to see available options.
                </p>
              </div>
            ) : currentQuestion?.type === "multi_select_nested" && currentOptions?.length > 0 ? (
              renderDropdownWithCheckboxes(currentOptions)
            ) : currentQuestion?.type === "nested_select" && currentOptions?.length > 0 ? (
              renderDropdowns(currentOptions, 0, [], currentQuestion?.key)
            ) : currentQuestion?.type === "checkbox" && currentQuestion?.options ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Filter out "other" option if allowOther is true - it will be shown separately */}
                {currentQuestion.options
                  .filter(opt => !(currentQuestion.allowOther && opt.value === "other"))
                  .map((opt, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <input
                        type="checkbox"
                        id={`checkbox_${currentQuestion.key}_${i}`}
                        checked={isOptionSelected(opt.value)}
                        onChange={() => handleCheckboxToggle(opt.value, opt.label)}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`checkbox_${currentQuestion.key}_${i}`}
                        style={{ fontSize: "16px", cursor: "pointer", flex: 1, userSelect: "none" }}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                {currentQuestion.allowOther && (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                      <input
                        type="checkbox"
                        id={`checkbox_${currentQuestion.key}_other`}
                        checked={isOptionSelected("other")}
                        onChange={() => handleCheckboxToggle("other", "Other")}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`checkbox_${currentQuestion.key}_other`}
                        style={{ fontSize: "16px", cursor: "pointer", userSelect: "none" }}
                      >
                        Other:
                      </label>
                    </div>
                    {isOptionSelected("other") && (
                      <input
                        type="text"
                        value={otherTextValues[currentQuestion.key] || ""}
                        onChange={(e) => handleOtherTextChange(currentQuestion.key, e.target.value)}
                        placeholder={currentQuestion.otherPlaceholder || "Please specify"}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "1px solid #ccc",
                          marginLeft: "30px"
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "radio" && currentQuestion?.options ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {currentQuestion.options.map((opt, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <input
                      type="radio"
                      name={currentQuestion.key}
                      checked={opt.value === "other" 
                        ? currentAnswer.startsWith("Other:") || currentAnswer === "Other"
                        : currentAnswer === opt.label}
                      onChange={() => {
                        if (opt.value === "other") {
                          handleRadioChange(opt.value, "Other");
                        } else {
                          handleRadioChange(opt.value, opt.label);
                        }
                      }}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        accentColor: "#2c68a1"
                      }}
                    />
                    <label style={{ fontSize: "16px", cursor: "pointer", flex: 1 }}>
                      {opt.label === "Other" ? "Other:" : opt.label}
                    </label>
                  </div>
                ))}
                {currentQuestion.allowOther && 
                 (currentAnswer === "Other" || currentAnswer.startsWith("Other:")) && (
                  <input
                    type="text"
                    value={currentAnswer.startsWith("Other:") 
                      ? currentAnswer.replace("Other:", "").trim() 
                      : otherTextValues[currentQuestion.key] || ""}
                    onChange={(e) => {
                      const otherText = e.target.value;
                      setOtherTextValues({
                        ...otherTextValues,
                        [currentQuestion.key]: otherText
                      });
                      setAnswers({
                        ...answers,
                        [currentQuestion?.question]: otherText ? `Other: ${otherText}` : "Other"
                      });
                    }}
                    placeholder={currentQuestion.otherPlaceholder || "Please specify"}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      fontSize: "16px",
                      borderRadius: "10px",
                      border: "1px solid #ccc",
                      marginLeft: "30px"
                    }}
                  />
                )}
              </div>
            ) : currentQuestion?.type === "multi_select" && currentQuestion?.options ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Filter out "other" option if allowOther is true - it will be shown separately */}
                {currentQuestion.options
                  .filter(opt => !(currentQuestion.allowOther && opt.value === "other"))
                  .map((opt, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <input
                        type="checkbox"
                        id={`multiselect_${currentQuestion.key}_${i}`}
                        checked={isOptionSelected(opt.value)}
                        onChange={() => handleCheckboxToggle(opt.value, opt.label)}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`multiselect_${currentQuestion.key}_${i}`}
                        style={{ fontSize: "16px", cursor: "pointer", flex: 1, userSelect: "none" }}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                {currentQuestion.allowOther && (
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                      <input
                        type="checkbox"
                        id={`multiselect_${currentQuestion.key}_other`}
                        checked={isOptionSelected("other")}
                        onChange={() => handleCheckboxToggle("other", "Other")}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#2c68a1"
                        }}
                      />
                      <label 
                        htmlFor={`multiselect_${currentQuestion.key}_other`}
                        style={{ fontSize: "16px", cursor: "pointer", userSelect: "none" }}
                      >
                        Other:
                      </label>
                    </div>
                    {isOptionSelected("other") && (
                      <input
                        type="text"
                        value={otherTextValues[currentQuestion.key] || ""}
                        onChange={(e) => handleOtherTextChange(currentQuestion.key, e.target.value)}
                        placeholder={currentQuestion.otherPlaceholder || "Please specify"}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "1px solid #ccc",
                          marginLeft: "30px"
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "domain_ratings" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {currentQuestion.domains && Array.isArray(currentQuestion.domains) && currentQuestion.domains.length > 0 ? (
                  currentQuestion.domains.map((domain, i) => {
                    const ratingOptions = currentQuestion.ratingOptions || [];
                    return (
                      <div key={i} style={{
                        padding: "15px",
                        border: "1px solid #ddd",
                        borderRadius: "10px",
                        backgroundColor: "#f9f9f9"
                      }}>
                        <div style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "12px",
                          color: "#2c68a1"
                        }}>
                          {domain.name}
                        </div>
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                          {ratingOptions.length > 0 ? (
                            ratingOptions.map((rating, j) => (
                              <div key={j} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <input
                                  type="radio"
                                  name={`domain_${domain.key}`}
                                  checked={getDomainRating(domain.key) === rating.value}
                                  onChange={() => handleDomainRatingChange(domain.key, rating.value, rating.label)}
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: "pointer",
                                    accentColor: "#2c68a1"
                                  }}
                                />
                                <label style={{ fontSize: "15px", cursor: "pointer" }}>
                                  {rating.label}
                                </label>
                              </div>
                            ))
                          ) : (
                            <div style={{ color: "#856404", padding: "10px", fontStyle: "italic" }}>
                              Rating options not configured
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ 
                    color: "#856404", 
                    padding: "15px", 
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "10px"
                  }}>
                    <strong>Configuration Issue:</strong> Domain ratings question is missing domain definitions.
                    {currentQuestion && (
                      <div style={{ marginTop: "10px", fontSize: "14px" }}>
                        Question key: {currentQuestion.key}<br/>
                        Has domains: {currentQuestion.domains ? "Yes" : "No"}<br/>
                        Has ratingOptions: {currentQuestion.ratingOptions ? "Yes" : "No"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : currentQuestion?.type === "number" ? (
              <input
                type="number"
                value={currentAnswer}
                onChange={handleNumberInputChange}
                onKeyDown={(e) => {
                  // Prevent 'e', 'E', '+', '-' keys for student age
                  if (currentQuestion?.key === "student_age") {
                    if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }
                }}
                min={currentQuestion?.key === "student_age" ? "0" : undefined}
                placeholder={currentQuestion.placeholder || "Enter a number"}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #ccc",
                  backgroundColor: "#fff"
                }}
              />
            ) : currentQuestion?.type === "text_input" ? (
              <input
                type="text"
                value={currentAnswer}
                onChange={handleTextInputChange}
                placeholder={currentQuestion.placeholder || "Enter text"}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #ccc",
                  backgroundColor: "#fff"
                }}
              />
            ) : currentQuestion?.type === "textarea" ? (
              <div>
                {/* <img src={EditCont} alt="edit icon" /> */}
                <TextareaAutosize className="textareacustome"
                  minRows={3}
                  maxRows={10}
                  style={{ border: "1px solid rgb(204, 204, 204)", width: "100%", resize: "none", borderRadius: "8px", height: "45px", overflow: "auto" }}
                  value={currentAnswer}
                  onChange={handleInputChange}
                  placeholder={currentQuestion.placeholder || "Write your answer..."}
                />
              </div>
            ) : currentQuestion?.type === "support_estimator" && currentQuestion?.fields ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {currentQuestion.fields.map((field, i) => {
                  const questionKey = currentQuestion.key;
                  const currentValue = supportEstimatorValues[questionKey]?.[field.key] || "";
                  
                  return (
                    <div key={i} style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "10px",
                      backgroundColor: "#f9f9f9"
                    }}>
                      <label style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        minWidth: "200px",
                        color: "#2c68a1"
                      }}>
                        {field.label}:
                      </label>
                      <input
                        type="number"
                        value={currentValue}
                        onChange={(e) => handleSupportEstimatorChange(field.key, e.target.value)}
                        placeholder="0"
                        min="0"
                        style={{
                          width: "120px",
                          padding: "10px 14px",
                          fontSize: "16px",
                          borderRadius: "10px",
                          border: "1px solid #ccc",
                          backgroundColor: "#fff"
                        }}
                      />
                      <span style={{ fontSize: "16px", color: "#666" }}>
                        {field.unit}
                      </span>
                    </div>
                  );
                })}
                {(() => {
                  const questionKey = currentQuestion.key;
                  const values = supportEstimatorValues[questionKey] || {};
                  const totalMinutesPerWeek = Object.values(values).reduce((sum, val) => sum + (val || 0), 0);
                  const totalHoursPerYear = (totalMinutesPerWeek * 40) / 60;
                  
                  return totalMinutesPerWeek > 0 ? (
                    <div style={{
                      padding: "15px",
                      backgroundColor: totalHoursPerYear >= 25 ? "#d4edda" : "#fff3cd",
                      border: `2px solid ${totalHoursPerYear >= 25 ? "#c3e6cb" : "#ffc107"}`,
                      borderRadius: "10px",
                      marginTop: "10px"
                    }}>
                      <div style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: totalHoursPerYear >= 25 ? "#155724" : "#856404",
                        marginBottom: "5px"
                      }}>
                        Estimated Total: {totalHoursPerYear.toFixed(1)} hours per school year
                      </div>
                      <div style={{
                        fontSize: "14px",
                        color: totalHoursPerYear >= 25 ? "#155724" : "#856404"
                      }}>
                        {totalHoursPerYear >= 25 
                          ? "✓ This meets the 25+ hours threshold for a School-Based IEP"
                          : "⚠ This is below the 25+ hours threshold. Consider a Classroom Support Plan."}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            ) : currentQuestion?.type === "date_select" ? (
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <select
                  value={currentAnswer.split(" ")[0] || ""}
                  onChange={(e) => {
                    const month = e.target.value;
                    const year = currentAnswer.split(" ")[1] || "";
                    setAnswers({
                      ...answers,
                      [currentQuestion?.question]: `${month} ${year}`.trim()
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    fontSize: "16px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value="">-- Select Month --</option>
                  {["January", "February", "March", "April", "May", "June", 
                    "July", "August", "September", "October", "November", "December"].map((month, i) => (
                    <option key={i} value={month}>{month}</option>
                  ))}
                </select>
                <select
                  value={currentAnswer.split(" ")[1] || ""}
                  onChange={(e) => {
                    const year = e.target.value;
                    const month = currentAnswer.split(" ")[0] || "";
                    setAnswers({
                      ...answers,
                      [currentQuestion?.question]: `${month} ${year}`.trim()
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    fontSize: "16px",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value="">-- Select Year --</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map((year, i) => (
                    <option key={i} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            ) : currentQuestion?.options && 
            currentQuestion.options.length > 0 && 
            !currentQuestion?.options.some((opt) => opt.children && opt.children.length > 0) ? (
              <select
                value={currentAnswer}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #ccc",
                  backgroundColor: "#fff",
                }}
              >
                <option value="">-- Select an option --</option>
                {currentQuestion.options.map((opt, i) => (
                  <option key={i} value={opt.label}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <div>
                {/* <img src={EditCont} alt="edit icon" /> */}
                <TextareaAutosize className="textareacustome"
                  minRows={1}
                  maxRows={6}
                  style={{ width: "100%", resize: "none", borderRadius: "8px", border: "1px solid rgb(204, 204, 204)",padding:"10px 14px" }}
                  value={currentAnswer}
                  onChange={handleInputChange}
                  placeholder={currentQuestion?.placeholder || "Write your answer..."}
                />
              </div>
            )}
          </div>

          <div className="button-next">
            {/* Back Button - Always show */}
            <button
              style={{
                backgroundColor: "rgba(44, 104, 161, 1)",
                border: "2px solid rgba(44, 104, 161, 1)",
                borderRadius: "12px",
                cursor: "pointer",
                marginRight: "10px",
                fontWeight: "600"
              }}
              onClick={handleBack}
            >
              <i className="fa fa-arrow-circle-left" aria-hidden="true" style={{ marginRight: "8px", marginLeft: "0" }} />
              Back
            </button>

            {/* Next/Submit Button */}
            <button
              style={{ 
                backgroundColor: "rgba(44, 104, 161, 1)",
                flex: "1"
              }}
              onClick={() => handleNext(currentQuestion?.important)}
            >
              {(() => {
                // For the designation question (has_designation), show "Next" by default
                // and "Learning Supports" only after an option is selected and it's the last question
                if (currentQuestion?.key === "has_designation") {
                  if (currentIndex === filteredQuestionsList.length - 1 && isAnswerFilled) {
                    return FinalButton;
                  }
                  return "Next ";
                }
                // For other questions, show "Next" or FinalButton based on index
                return currentIndex < filteredQuestionsList.length - 1 ? "Next " : FinalButton;
              })()}
              <i className="fa fa-arrow-circle-right" aria-hidden="true" />
            </button>

            {/* Skip Button */}

            {currentQuestion?.important === "false" &&
              currentIndex < filteredQuestionsList.length - 1 && (
                <button
                  style={{
                    color: "rgba(44, 104, 161, 1)",
                    border: "none",
                    backgroundColor: "transparent",
                    float: "right",
                    marginRight: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleNext(currentQuestion?.important)}
                >
                  Skip{" "}
                  <i
                    className="fa fa-arrow-right simplearrow"
                    aria-hidden="true"
                  ></i>
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentInner2;